import { Controller } from "stimulus"

const HEADINGS = ["heading1", "heading2", "heading3"]

// Barre latérale fixe de l'éditeur d'articles du blog.
// Un éditeur Trix ne peut être relié qu'à une seule toolbar : la barre latérale
// relaie donc chaque clic vers le bouton correspondant de la vraie toolbar (id
// passé via data-toolbar-id) et reflète en retour l'état actif/désactivé.
// Les titres (H1/H2/H3) sont pilotés directement via l'API de l'éditeur (comme
// slack_rich_text_controller lit `trixeditor.editor`), avec un vrai
// comportement de « sélecteur de titre » appliqué AUSSI à la toolbar classique.
export default class extends Controller {
  connect() {
    this.toolbar = document.getElementById(this.element.dataset.toolbarId)
    if (!this.toolbar) return
    this.syncState = this.syncState.bind(this)
    this.observer = new MutationObserver(this.syncState)
    this.observer.observe(this.toolbar, {
      attributes: true,
      subtree: true,
      attributeFilter: ["class", "disabled", "data-trix-active"]
    })
    // Toolbar classique : intercepte le mousedown de ses boutons H1/H2/H3 en
    // phase de capture pour exécuter notre logique et court-circuiter Trix.
    this.interceptNativeHeadings = this.interceptNativeHeadings.bind(this)
    this.toolbar.addEventListener("mousedown", this.interceptNativeHeadings, true)
    this.syncState()
  }

  disconnect() {
    if (this.observer) this.observer.disconnect()
    if (this.toolbar) this.toolbar.removeEventListener("mousedown", this.interceptNativeHeadings, true)
  }

  // Lu à chaque événement (jamais mis en cache). L'éditeur Trix peut ne pas être encore initialisé quand le contrôleur se connecte.
  get editor() {
    const el = document.querySelector(`trix-editor[toolbar="${this.element.dataset.toolbarId}"]`)
    return el && el.editor
  }

  // Garde la sélection de l'éditeur quand on clique dans la barre latérale.
  keepSelection(event) {
    event.preventDefault()
  }

  // Toolbar classique : reprend la main sur les boutons de titre uniquement.
  interceptNativeHeadings(event) {
    const button = event.target.closest("[data-trix-attribute]")
    if (!button || !this.toolbar.contains(button)) return
    const attribute = button.getAttribute("data-trix-attribute")
    if (!HEADINGS.includes(attribute)) return
    const editor = this.editor
    if (!editor) return
    event.preventDefault()
    event.stopPropagation()
    this.applyHeading(editor, attribute)
  }

  // Barre latérale : relaie le clic vers l'action correspondante.
  relay(event) {
    if (!this.toolbar) return
    const realButton = this.toolbar.querySelector(event.currentTarget.dataset.proxySelector)
    if (!realButton || realButton.disabled) return

    const editor = this.editor
    const attribute = realButton.getAttribute("data-trix-attribute")
    const action = realButton.getAttribute("data-trix-action")

    // Attributs simples (gras, italique, titres, citation, listes…) : on pilote
    // l'éditeur directement. Le lien (href) a une action/dialogue : on l'exclut.
    if (editor && attribute && !action && attribute !== "href") {
      if (HEADINGS.includes(attribute)) {
        this.applyHeading(editor, attribute)
      } else {
        if (editor.attributeIsActive(attribute)) {
          editor.deactivateAttribute(attribute)
        } else {
          editor.activateAttribute(attribute)
        }
        this.syncState()
      }
      return
    }

    // Actions (annuler, joindre, retrait…) et lien : Trix 1.x les branche sur
    // `click`. On rejoue la séquence complète d'un vrai clic souris.
    ;["mousedown", "click"].forEach((type) => {
      realButton.dispatchEvent(new MouseEvent(type, { bubbles: true, cancelable: true, view: window }))
    })
  }

  // Sélecteur de titre : cliquer un H applique CE titre en remplaçant tout autre
  // titre déjà présent. Trix interdit d'appliquer un attribut de bloc à un bloc
  // « terminal » (canSetCurrentBlockAttribute = !isTerminalBlock), or un titre
  // EST terminal. Impossible donc de passer d'un titre à un autre directement :
  // on retire d'abord les trois titres INCONDITIONNELLEMENT (deactivateAttribute
  // est un no-op si l'attribut est absent) pour rendre le bloc non-terminal, PUIS
  // on applique le titre cliqué — sauf s'il était le seul actif (→ retrait).
  applyHeading(editor, attribute) {
    const removeIt =
      editor.attributeIsActive(attribute) &&
      HEADINGS.every((h) => h === attribute || !editor.attributeIsActive(h))
    HEADINGS.forEach((h) => editor.deactivateAttribute(h))
    if (!removeIt) editor.activateAttribute(attribute)
    this.syncState()
  }

  scrollTop() {
    window.scrollTo({ top: 0, behavior: "smooth" })
  }

  scrollBottom() {
    window.scrollTo({ top: document.documentElement.scrollHeight, behavior: "smooth" })
  }

  syncState() {
    if (!this.toolbar) return
    this.element.querySelectorAll("[data-proxy-selector]").forEach((button) => {
      const real = this.toolbar.querySelector(button.dataset.proxySelector)
      if (!real) return
      button.classList.toggle("trix-active", real.classList.contains("trix-active"))
      button.disabled = real.disabled
    })
  }
}
